/**
 * Chart.js rendering module for SalaryLab.
 * Tree-shakes Chart.js to only import needed components.
 */

// IMPORTANT: Import native_computed_style BEFORE chart.js.
// When the ESM bundle is loaded as a classic <script>, Chart.js's
// `var getComputedStyle` leaks to the global scope and overrides
// window.getComputedStyle with a self-referencing wrapper, causing
// infinite recursion. We capture the native function first, then
// restore it after Chart.js initialises.
import { nativeGetComputedStyle } from "../../utils/native_computed_style"

import {
  Chart,
  DoughnutController,
  BarController,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js"

// Restore native getComputedStyle if Chart.js has overwritten it
if (window.getComputedStyle !== nativeGetComputedStyle) {
  window.getComputedStyle = nativeGetComputedStyle
}

// Register only the components we use
Chart.register(
  DoughnutController,
  BarController,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
)

const COLORS = {
  takeHome: "#0E7490",     // cyan-700
  paye: "#7C3AED",         // violet-600
  acc: "#059669",          // emerald-600
  kiwisaver: "#D97706",    // amber-600
  studentLoan: "#E11D48",  // rose-600
  ietc: "#2563EB",         // blue-600
}

/**
 * Render a donut chart showing salary breakdown.
 * @param {HTMLCanvasElement} canvas
 * @param {Object} data - Calculation result
 * @returns {Chart} Chart instance
 */
export function renderDonutChart(canvas, data) {
  const existing = Chart.getChart(canvas)
  if (existing) existing.destroy()

  const { segments, labels, colors } = buildDonutData(data)

  return new Chart(canvas, {
    type: "doughnut",
    data: {
      labels,
      datasets: [{
        data: segments,
        backgroundColor: colors,
        borderWidth: 0,
        hoverOffset: 4,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      cutout: "65%",
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 16,
            usePointStyle: true,
            pointStyleWidth: 10,
            font: { size: 12, family: "Inter, sans-serif" },
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0
              return ` ${context.label}: $${value.toLocaleString()} (${pct}%)`
            },
          },
        },
      },
    },
  })
}

/**
 * Render a horizontal stacked bar chart showing tax bracket waterfall.
 * @param {HTMLCanvasElement} canvas
 * @param {Array} breakdown - Tax breakdown array from calculation result
 * @returns {Chart} Chart instance
 */
export function renderBracketChart(canvas, breakdown) {
  const existing = Chart.getChart(canvas)
  if (existing) existing.destroy()

  if (!breakdown || breakdown.length === 0) return null

  const labels = breakdown.map(b => b.description || `${b.rate}%`)
  const taxAmounts = breakdown.map(b => b.tax_component)
  const residuals = breakdown.map(b => (b.attributable_income || 0) - (b.tax_component || 0))

  return new Chart(canvas, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Tax",
          data: taxAmounts,
          backgroundColor: COLORS.paye,
        },
        {
          label: "After Tax",
          data: residuals,
          backgroundColor: COLORS.takeHome,
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          ticks: {
            callback: (value) => `$${(value / 1000).toFixed(0)}K`,
            font: { size: 11, family: "Inter, sans-serif" },
          },
          grid: { display: false },
        },
        y: {
          stacked: true,
          ticks: {
            font: { size: 11, family: "Inter, sans-serif" },
          },
          grid: { display: false },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 12,
            usePointStyle: true,
            pointStyleWidth: 10,
            font: { size: 12, family: "Inter, sans-serif" },
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => ` ${context.dataset.label}: $${context.parsed.x.toLocaleString()}`,
          },
        },
      },
    },
  })
}

/**
 * Update existing chart data without destroying/recreating.
 * @param {Chart} chart
 * @param {Object} data - New calculation result
 */
export function updateDonutChart(chart, data) {
  if (!chart) return

  const { segments, labels } = buildDonutData(data)

  chart.data.labels = labels
  chart.data.datasets[0].data = segments
  chart.update("none")
}

function buildDonutData(data) {
  const segments = []
  const labels = []
  const colors = []

  // Detect data format: PAYE vs contractor
  const isContractor = data.net_take_home != null
  const netAmount = isContractor ? data.net_take_home : data.net_salary
  const taxAmount = isContractor ? data.paye_tax : data.total_tax
  const ksAmount = isContractor ? data.kiwisaver_voluntary : data.kiwisaver_employee

  if (netAmount > 0) {
    segments.push(netAmount)
    labels.push("Take-Home Pay")
    colors.push(COLORS.takeHome)
  }
  if (taxAmount > 0) {
    segments.push(taxAmount)
    labels.push("Income Tax")
    colors.push(COLORS.paye)
  }
  if (data.acc_levy > 0) {
    segments.push(data.acc_levy)
    labels.push("ACC Levy")
    colors.push(COLORS.acc)
  }
  if (ksAmount > 0) {
    segments.push(ksAmount)
    labels.push("KiwiSaver")
    colors.push(COLORS.kiwisaver)
  }
  if (data.student_loan > 0) {
    segments.push(data.student_loan)
    labels.push("Student Loan")
    colors.push(COLORS.studentLoan)
  }
  if (isContractor && data.business_expenses > 0) {
    segments.push(data.business_expenses)
    labels.push("Business Expenses")
    colors.push("#6B7280") // gray-500
  }

  return { segments, labels, colors }
}
